// C6 (docs/PAID_CONTENT.md, mockups docs/mockups/packs.html): which islands are wrapped for this
// family, and the children's asks to unwrap them. A country's islands are wrapped until the family
// owns its pack, except the pack's free island. Before the phone has ever heard what the family
// owns (`owned` null: offline on the first start), nothing is wrapped: the server is the real gate.
import type { TripEvent } from './trip/project'

export interface CatalogueEntry {
  id: string
  name: string
  country: string
  status: 'live' | 'coming-soon' | 'hidden'
  freeIslandId: string
  freeLanguageUnitId: string
  sku: string
  prices: Record<string, number>
}

/** The pack an island's country is sold in (shown in the Atlas: live or coming soon). */
export const packOfCountry = (catalogue: readonly CatalogueEntry[], country: string | undefined) =>
  country ? catalogue.find((p) => p.country === country && p.status !== 'hidden') : undefined

/** The pack that unwraps this island, when the family doesn't have it; null when it's theirs to play. */
export function wrappedBy(o: { catalogue: readonly CatalogueEntry[]; owned: readonly string[] | null; islandId: string | null; country: string | undefined }): CatalogueEntry | null {
  if (!o.owned || !o.islandId) return null
  const pack = packOfCountry(o.catalogue, o.country)
  if (!pack || o.owned.includes(pack.id) || pack.freeIslandId === o.islandId) return null
  return pack
}

export interface PackAsk {
  key: string
  packId: string
  childId: string
  at: number
}

/** The packs children asked for (`pack.asked`) that the family doesn't own yet: one per child and pack. */
export function packAsks(events: readonly TripEvent[], owned: readonly string[], players: readonly string[]): PackAsk[] {
  const asks = new Map<string, PackAsk>()
  for (const e of events) {
    if (e.type !== 'pack.asked' || !players.includes(e.personId)) continue
    const packId = (e.payload as { packId?: unknown } | null)?.packId
    if (typeof packId !== 'string' || owned.includes(packId)) continue
    const key = `pack:${packId}:${e.personId}`
    if (!asks.has(key)) asks.set(key, { key, packId, childId: e.personId, at: e.at })
  }
  return [...asks.values()]
}

/** Has this person already asked for this pack (the button then says so)? */
export const askedFor = (events: readonly TripEvent[], personId: string, packId: string) =>
  events.some((e) => e.type === 'pack.asked' && e.personId === personId && (e.payload as { packId?: unknown } | null)?.packId === packId)

/** A price in minor units, as the phone's locale writes it (A$14.99). */
export const formatPrice = (minor: number, currency: string, locale?: string) =>
  new Intl.NumberFormat(locale, { style: 'currency', currency }).format(minor / 100)

/** The web price to show: the phone's own currency when the pack has one, else USD. */
export function webPrice(pack: CatalogueEntry, locale: string | undefined): { minor: number; currency: string } | null {
  const region = locale?.split('-')[1]?.toUpperCase()
  const local = region === 'AU' ? 'AUD' : region === 'NZ' ? 'NZD' : region === 'GB' ? 'GBP' : region === 'US' ? 'USD' : undefined
  const currency = local && pack.prices[local] ? local : pack.prices.USD ? 'USD' : Object.keys(pack.prices)[0]
  return currency ? { minor: pack.prices[currency]!, currency } : null
}
